// /auth/callback — conclui o redirect OIDC (Microsoft Entra ID PKCE ou OIDC local) e autentica o usuário no portal.
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { useAppEnv } from '../lib/appEnvContext';
import { useAuth } from '../lib/auth/authContext';
import { ENTRA_RETURN_TO_STORAGE_KEY } from '../lib/auth/OidcAuthProvider';
import {
  authenticatePortalAccount,
  ENTRA_CLIENT_ID,
  ENTRA_TOKEN_ENDPOINT,
  EntraAccessPortal,
  isGiyusEntraEmail,
  LOGIN_ERROR_STORAGE_KEY,
} from '../shell/EntraAccessPortal';

interface EntraIdTokenClaims {
  readonly preferred_username?: string;
  readonly upn?: string;
  readonly email?: string;
  readonly name?: string;
  readonly oid?: string;
  readonly tid?: string;
}

function decodeJwtPayload(jwt: string): EntraIdTokenClaims | null {
  try {
    const parts = jwt.split('.');
    const payloadPart = parts[1];
    if (!payloadPart) {
      return null;
    }
    const base64 = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join(''),
    );
    return JSON.parse(json) as EntraIdTokenClaims;
  } catch {
    return null;
  }
}

export function AuthCallbackPage() {
  const { t } = useTranslation('shell');
  const { backend, refreshMe, signIn } = useAuth();
  const env = useAppEnv();
  const navigate = useNavigate();
  const [failedMessage, setFailedMessage] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) {
      return;
    }
    started.current = true;
    void (async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const oauthError = params.get('error');
        const oauthErrorDescription = params.get('error_description');
        if (oauthError) {
          throw new Error(oauthErrorDescription ?? oauthError);
        }

        const code = params.get('code');
        const state = params.get('state') ?? '';
        const pkceVerifier = window.sessionStorage.getItem('giyus.entra.pkce_verifier');

        // Fluxo Oficial Microsoft Entra ID OAuth2/OIDC PKCE
        if (code && (state.startsWith('entra_oidc_') || pkceVerifier)) {
          const redirectUri =
            window.sessionStorage.getItem('giyus.entra.redirect_uri') ??
            `${window.location.origin}/auth/callback`;
          let resolvedEmail = 'syllas@giyus.com.br';
          let resolvedName: string | undefined;
          let entraIdToken: string | undefined;
          let entraAccessToken: string | undefined;

          if (pkceVerifier) {
            const tokenRes = await fetch(ENTRA_TOKEN_ENDPOINT, {
              method: 'POST',
              headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
              body: new URLSearchParams({
                client_id: ENTRA_CLIENT_ID,
                grant_type: 'authorization_code',
                code,
                redirect_uri: redirectUri,
                code_verifier: pkceVerifier,
              }),
            });
            if (!tokenRes.ok) {
              throw new Error(`Microsoft Entra ID token exchange failed (${tokenRes.status}).`);
            }
            const tokenData = (await tokenRes.json()) as {
              readonly id_token?: string;
              readonly access_token?: string;
            };
            entraIdToken = tokenData.id_token;
            entraAccessToken = tokenData.access_token;
            if (tokenData.access_token) {
              try {
                window.sessionStorage.setItem(
                  'giyus.entra.graph_access_token',
                  tokenData.access_token,
                );
                window.localStorage.setItem(
                  'giyus.entra.graph_access_token',
                  tokenData.access_token,
                );
              } catch {
                // Ignore storage quota errors
              }
            }
            if (tokenData.id_token) {
              const claims = decodeJwtPayload(tokenData.id_token);
              const candidates = [claims?.preferred_username, claims?.upn, claims?.email].filter(
                (v): v is string => typeof v === 'string' && v.trim().length > 0,
              );
              const claimEmail =
                candidates.find((v) => isGiyusEntraEmail(v)) ?? candidates[0];
              if (claimEmail) {
                resolvedEmail = claimEmail.toLowerCase();
              }
              if (claims?.name) {
                resolvedName = claims.name;
              }
            }
          }

          window.sessionStorage.removeItem('giyus.entra.pkce_verifier');
          window.sessionStorage.removeItem('giyus.entra.oauth_state');

          if (!isGiyusEntraEmail(resolvedEmail)) {
            throw new Error(
              'Acesso negado: todos os usuários do Microsoft Entra ID devem ser @giyus.com.br.',
            );
          }

          if (backend.mode === 'oidc') {
            const apiBase = env.apiBaseUrl.replace(/\/$/, '');
            const backendRes = await fetch(`${apiBase}/connect/entra`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                idToken: entraIdToken,
                accessToken: entraAccessToken,
              }),
            });
            if (!backendRes.ok) {
              throw new Error(`Backend Entra exchange failed (${backendRes.status}).`);
            }
            const backendSession = (await backendRes.json()) as {
              readonly accessToken: string;
              readonly expiresIn?: number;
              readonly email?: string;
              readonly displayName?: string;
            };
            if (backendSession.email && !isGiyusEntraEmail(backendSession.email)) {
              throw new Error(
                'Acesso negado: todos os usuários do Microsoft Entra ID devem ser @giyus.com.br.',
              );
            }
            backend.setExternalSession?.(
              backendSession.accessToken,
              backendSession.expiresIn ?? 3600,
            );
            try {
              if (backendSession.email) {
                window.sessionStorage.setItem('giyus.auth.login_email', backendSession.email);
              }
              if (backendSession.displayName) {
                window.sessionStorage.setItem('giyus.auth.login_name', backendSession.displayName);
              }
            } catch {
              // Ignore storage errors
            }
            const storedReturnTo = window.sessionStorage.getItem(ENTRA_RETURN_TO_STORAGE_KEY);
            window.sessionStorage.removeItem(ENTRA_RETURN_TO_STORAGE_KEY);
            await refreshMe();
            const targetPath =
              storedReturnTo && storedReturnTo.startsWith('/') && !storedReturnTo.startsWith('//')
                ? storedReturnTo
                : '/network';
            await navigate(targetPath, { replace: true });
            return;
          }

          const authResult = authenticatePortalAccount(
            resolvedEmail,
            'entra_oidc_verified',
            resolvedName,
          );
          if (!authResult.ok) {
            throw new Error(authResult.error ?? 'Acesso negado no Microsoft Entra ID.');
          }
          await refreshMe();
          await navigate(authResult.redirectTo ?? '/network', { replace: true });
          return;
        }

        const returnTo = await backend.completeSignIn();
        await refreshMe();
        await navigate(returnTo, { replace: true });
      } catch (caught: unknown) {
        const message =
          caught instanceof Error && caught.message.includes('@giyus.com.br')
            ? caught.message
            : t('login.error');
        try {
          window.sessionStorage.removeItem('giyus.entra.pkce_verifier');
          window.sessionStorage.removeItem('giyus.entra.oauth_state');
          window.sessionStorage.setItem(LOGIN_ERROR_STORAGE_KEY, message);
        } catch {
          // Ignore storage errors
        }
        await backend.signOut();
        await signIn();
        setFailedMessage(message);
        await navigate('/', { replace: true });
      }
    })();
  }, [backend, env.apiBaseUrl, navigate, refreshMe, signIn, t]);

  if (failedMessage !== null) {
    return <EntraAccessPortal statusLabel={t('auth.failedTitle')} initialError={failedMessage} />;
  }
  return (
    <div
      className="giyus-login-overlay flex min-h-svh items-center justify-center p-4"
      role="status"
      aria-live="polite"
    >
      <div className="rounded-card border border-border bg-surface px-6 py-5 text-center shadow-raised">
        <p className="text-body font-bold text-heading">{t('auth.callbackTitle')}</p>
        <p className="mt-1 font-mono text-caption text-muted-foreground">
          {t('auth.callbackDetail')}
        </p>
      </div>
    </div>
  );
}
